// PC 首页文案字典：zh 为现有文案基准，en 为营销英语；各 Section 客户端组件经 props 接收对应切片
export interface HomeBannerSlide {
    // Banner 轮播标语：主标语 / 副标题 / 说明
    title1: string
    title2: string
    title3: string
}

export interface HomeBannerDict {
    slides: HomeBannerSlide[]
    // 双 CTA：主按钮 → /contact2，次按钮 → /gamelist2（路由固定在组件内）
    ctaPrimary: string
    ctaSecondary: string
}

export interface HomeFeatureCard {
    title: string
    desc: string
}

export interface HomeContactMethod {
    title: string
    contact: string
}

export interface HomeSection1Dict {
    kicker: string
    heading: string
    category: string
    features: HomeFeatureCard[]
    contacts: HomeContactMethod[]
}

export interface HomeSection2Dict {
    title: string
    // 「进入内容库」链接文案 → /gamelist2
    viewAll: string
}

// 能力矩阵 TAB：每个 TAB 3~4 个能力点
export interface HomeCapabilityTab {
    tab: string
    points: HomeFeatureCard[]
}

export interface HomeSection41Dict {
    kicker: string
    heading: string
    sub: string
    tabs: HomeCapabilityTab[]
}

// 「社交+游戏」场景融合区：场景 TAB + 竖版短视频切片轮播
export interface HomeFusionClip {
    video: string
    poster: string
    // 游戏切片按 gameid 解析本地化游戏名，本字段兜底；非游戏切片直接显示本字段
    title: string
    gameid?: number
}

export interface HomeFusionTab {
    name: string
    clips: HomeFusionClip[]
}

export interface HomeFusionDict {
    kicker: string
    heading: string  // 多种"社交+游戏"场景深度融合
    sub: string
    tabs: HomeFusionTab[]
}

export interface HomeSection6Dict {
    heading: string
    viewAll: string
    detail: string
    disclaimer: string
    // 运营方案摘要区块：数据为 solution_tabs 前 3 条（服务端读库传入）
    solutions: {
        kicker: string
        heading: string
        viewAll: string
        // 步骤数徽标模板，{count} 为占位符
        stepsLabel: string
    }
}

// 数据背书：games/categories 数值由 page.tsx 读库传入，languages/nodes 数值写字典
export interface HomeAccelStatFromDb {
    suffix: string
    label: string
}

export interface HomeAccelStatStatic {
    value: number
    suffix: string
    label: string
}

export interface HomeAccelDict {
    kicker: string
    heading: string
    sub: string
    // 地图枢纽与节点城市名（顺序对应组件内 ACCEL_NODES 经纬度数组）
    hub: string
    cities: string[]
    stats: {
        games: HomeAccelStatFromDb
        categories: HomeAccelStatFromDb
        languages: HomeAccelStatStatic
        nodes: HomeAccelStatStatic
    }
    footnote: string
}

// 客户证言：首批为脱敏示例文案（公司名以「某 XX 平台」代称），后台化列入 P2
export interface HomeTestimonial {
    quote: string
    company: string
    role: string
}

export interface HomeTestimonialsDict {
    kicker: string
    heading: string
    items: HomeTestimonial[]
}

export interface HomeCtaDict {
    title: string
    button: string
}

export interface HomeDict {
    metadata: {
        title: string
        description: string
    }
    banner: HomeBannerDict
    section1: HomeSection1Dict
    section2: HomeSection2Dict
    section41: HomeSection41Dict
    fusion: HomeFusionDict
    section6: HomeSection6Dict
    accel: HomeAccelDict
    testimonials: HomeTestimonialsDict
    cta: HomeCtaDict
}

export const homeDict: { zh: HomeDict; en: HomeDict } = {
    zh: {
        metadata: {
            title: 'YYGame-直播互动新体验',
            description: 'YYGame,直播互动新体验',
        },
        banner: {
            slides: [
                {
                    title1: '游戏化互动，驱动增长',
                    title2: '一站式互动游戏内容与运营解决方案',
                    title3: '全品类覆盖，助力平台活跃与营收双增长！',
                },
                {
                    title1: '让互动成为增长核动力！',
                    title2: '全品类休闲游戏生态+定制化解决方案',
                    title3: '深度定制服务，解锁商业新场景，小游戏也可以脑洞大开！',
                },
                {
                    title1: '全球部署，合规出海',
                    title2: '多语言内容包 + 全球加速网络',
                    title3: '多语种内容本地化，五大洲加速节点智能调度，让全球玩家零卡顿！',
                },
            ],
            ctaPrimary: '免费获取方案',
            ctaSecondary: '查看全部游戏',
        },
        section1: {
            kicker: '产品玩法库',
            heading: 'Product Gameplay Library',
            category: '弹幕游戏',
            features: [
                { title: '萌宠斗蛋仔（经典红蓝）', desc: '发送弹幕指令出兵，哪方阵营先打败神龙获胜。' },
                { title: '保卫公主（趣味对抗）', desc: '发送弹幕指令抢夺公主，速度与激情的极限拉扯。' },
                { title: '出海游戏专场', desc: '专为开发者定制，多语言文化融合海外专区。' },
            ],
            contacts: [
                { title: '公司位置', contact: '中国 · 上海' },
                { title: '商务电话', contact: '（+86）188 0038 2271' },
                { title: '商务邮箱', contact: 'vip@yygame.tech' },
            ],
        },
        section2: {
            title: '热门游戏',
            viewAll: '进入内容库',
        },
        section41: {
            kicker: '能力矩阵',
            heading: '一站式游戏化互动能力',
            sub: '从内容到运营、从接入到出海，五大能力模块覆盖游戏化互动全链路。',
            tabs: [
                {
                    tab: '弹幕互动',
                    points: [
                        { title: '弹幕指令出兵', desc: '观众发送弹幕指令实时操控战局，人人都是局内人。' },
                        { title: '礼物左右战局', desc: '礼物特效转化为游戏技能，打赏即参与。' },
                        { title: '红蓝阵营对抗', desc: '直播间分阵营对抗，胜负欲驱动弹幕刷屏。' },
                        { title: '主播 PK 连线', desc: '主播实时连线对战，观众弹幕与礼物决定胜负。' },
                    ],
                },
                {
                    tab: '休闲竞技',
                    points: [
                        { title: '经典棋牌全品类', desc: '斗地主、掼蛋、麻将等国民玩法开箱即用。' },
                        { title: '实时多人对战', desc: '1v1、2v2 与多人房，毫秒级状态同步。' },
                        { title: '轻量即点即玩', desc: 'H5 免下载秒开，低配设备流畅运行。' },
                        { title: '排行榜竞技', desc: '天梯排行与赛季结算，持续刺激回流。' },
                    ],
                },
                {
                    tab: '运营玩法',
                    points: [
                        { title: '点单约玩', desc: '付费点单主播陪玩，构建高频付费场景。' },
                        { title: '门票房与竞猜', desc: '以小博大，门票与竞猜双引擎营收。' },
                        { title: '联赛赛季体系', desc: '赛事话题拉长活跃周期，沉淀核心玩家。' },
                        { title: '跨房跨域匹配', desc: '跨直播间 PK 组局，盘活全站流量。' },
                    ],
                },
                {
                    tab: '全球加速',
                    points: [
                        { title: '全球节点调度', desc: '五大洲加速节点，智能调度最优链路。' },
                        { title: '低延迟对战同步', desc: '毫秒级状态同步，跨国对战零卡顿。' },
                        { title: '区域化部署', desc: '按目标市场就近部署，合规出海。' },
                    ],
                },
                {
                    tab: '多语言',
                    points: [
                        { title: '多语言内容包', desc: '简中、繁中、英、日等多语种随需切换。' },
                        { title: '区域化内容定制', desc: '玩法、文案、美术按目标市场本地化。' },
                        { title: '一键翻译工作台', desc: '后台一键翻译，新语种快速上线。' },
                    ],
                },
            ],
        },
        fusion: {
            kicker: '场景融合',
            heading: '多种“社交+游戏”场景深度融合',
            sub: '游戏化内容深度嵌入各类社交场景',
            tabs: [
                {
                    name: '语音社交',
                    clips: [
                        { video: '/video/保卫公主.mp4', poster: '/img/gamelistpic2/保卫公主.webp', title: '保卫公主', gameid: 956 },
                        { video: '/video/萌宠斗蛋仔.mp4', poster: '/img/gamelistpic2/萌宠斗蛋仔.webp', title: '萌宠斗蛋仔', gameid: 944 },
                    ],
                },
                {
                    name: '视频社交',
                    clips: [
                        { video: '/video/雷电风云战机.mp4', poster: '/img/gamelistpic2/雷电风云战机.webp', title: '雷电风云战机', gameid: 920 },
                        { video: '/video/奥林匹斯之门.mp4', poster: '/img/gamelistpic2/奥林匹斯.webp', title: '奥林匹斯', gameid: 523 },
                    ],
                },
                {
                    name: '1V1社交',
                    clips: [
                        { video: '/video/玩转世界杯.mp4', poster: '/img/gamelistpic2/玩转世界杯.webp', title: '玩转世界杯', gameid: 528 },
                        { video: '/img/section41/YYGame41.mp4', poster: '/img/gamelistpic2/斗地主横版.webp', title: '斗地主' },
                    ],
                },
                {
                    name: '才艺社交',
                    clips: [
                        { video: '/img/section41/YYGame42.mp4', poster: '/img/gamelistpic2/骗子酒馆.webp', title: '骗子酒馆' },
                        { video: '/img/section41/YYGame19.mp4', poster: '/img/gamelistpic2/你画我猜.webp', title: '你画我猜' },
                    ],
                },
            ],
        },
        section6: {
            heading: '热门玩法',
            viewAll: '查看全部',
            detail: '查看游戏详情',
            disclaimer: '此demo仅用于游戏演示，部分游戏无法在中国大陆境内/或境外部分国家/地区接入和运营，请按照相应国家/地区可适用的法律法规要求选择适当的游戏接入和运营。',
            solutions: {
                kicker: '运营方案',
                heading: '成熟的运营玩法，开箱即用',
                viewAll: '查看全部方案',
                stepsLabel: '{count} 步流程',
            },
        },
        accel: {
            kicker: 'Global Game Acceleration',
            heading: '全球部署 · 游戏加速网络',
            sub: '加速节点遍布全球，智能调度最优链路，无论身处何地，皆能体验丝滑',
            hub: '香港枢纽',
            cities: ['洛杉矶', '法兰克福', '新加坡', '东京', '圣保罗', '悉尼', '迪拜', '孟买', '伦敦', '首尔'],
            stats: {
                games: { suffix: ' 款', label: '服务游戏内容' },
                categories: { suffix: ' 个', label: '游戏内容分类' },
                languages: { value: 12, suffix: ' 种', label: '覆盖语种' },
                nodes: { value: 2800, suffix: '+', label: '全球加速节点' },
            },
            footnote: '以香港为核心枢纽，辐射洛杉矶 · 伦敦 · 法兰克福 · 东京 · 首尔 · 新加坡 · 迪拜 · 孟买 · 圣保罗 · 悉尼等全球节点',
        },
        testimonials: {
            kicker: '客户证言',
            heading: '全球合作伙伴都在用',
            items: [
                { quote: '接入弹幕玩法后，直播间互动率提升明显，观众平均停留时长翻了近一倍，从对接到上线只用了一周。', company: '某头部语音社交平台', role: '运营总监' },
                { quote: '多语言内容包和区域化部署让我们在中东市场快速上线，节日运营活动拉新成本降了三成。', company: '某出海直播 App', role: '产品负责人' },
                { quote: '门票房加竞猜的组合玩法让付费渗透率显著提升，是目前我们 ROI 最高的运营手段。', company: '某社交娱乐平台', role: '商业化负责人' },
                { quote: '点单约玩上线后主播留存和收入双增长，平台抽成模型跑得很健康，主播主动要求加开场次。', company: '某语音房 App', role: '增长负责人' },
                { quote: 'SDK 接入比预期简单，三天完成联调，全球加速节点让跨国对战几乎无感知延迟。', company: '某海外休闲游戏平台', role: 'CTO' },
                { quote: '内容库更新快、品类全，运营位排期灵活，是我们游戏化内容的长期供应方。', company: '某头部直播平台', role: '生态合作总监' },
            ],
        },
        cta: {
            title: '准备好开启游戏化增长了吗？',
            button: '免费获取方案',
        },
    },
    en: {
        metadata: {
            title: 'YYGame - Interactive Mini Games for Live Streaming',
            description: 'YYGame focuses on casual game innovation: danmaku games, voice-room interaction and gamified marketing solutions, accelerated across 150+ countries and regions.',
        },
        banner: {
            slides: [
                {
                    title1: 'Gamified Interaction That Drives Growth',
                    title2: 'One-stop interactive game content and operations',
                    title3: 'Full-category coverage that lifts both engagement and revenue.',
                },
                {
                    title1: 'Interaction That Powers Growth',
                    title2: 'A full-category casual game ecosystem plus tailored solutions',
                    title3: 'Deep customization unlocks new business scenarios — even mini games can be boldly imaginative.',
                },
                {
                    title1: 'Global Deployment, Compliant Expansion',
                    title2: 'Multi-language content packs plus a global acceleration network',
                    title3: 'Localized content in many languages and intelligent routing across five continents — stutter-free gameplay for players everywhere.',
                },
            ],
            ctaPrimary: 'Get a Free Proposal',
            ctaSecondary: 'Browse All Games',
        },
        section1: {
            kicker: 'Our Games',
            heading: 'Product Gameplay Library',
            category: 'Danmaku Games',
            features: [
                { title: 'Pet Brawl · Classic Red vs Blue', desc: 'Spawn troops with danmaku commands — the first side to slay the dragon wins.' },
                { title: 'Guard the Princess · Fun Duel', desc: 'Race to seize the princess with danmaku commands — a fast and furious tug-of-war.' },
                { title: 'Global Games Showcase', desc: 'A tailor-made zone for developers, blending multi-language content for overseas markets.' },
            ],
            contacts: [
                { title: 'Our Office', contact: 'Shanghai, China' },
                { title: 'Business Phone', contact: '(+86) 188 0038 2271' },
                { title: 'Business Email', contact: 'vip@yygame.tech' },
            ],
        },
        section2: {
            title: 'Featured Games',
            viewAll: 'Explore the Library',
        },
        section41: {
            kicker: 'Capability Matrix',
            heading: 'One-Stop Gamified Interaction',
            sub: 'From content to operations, integration to global launch — five capability modules covering the full gamification lifecycle.',
            tabs: [
                {
                    tab: 'Danmaku Interaction',
                    points: [
                        { title: 'Danmaku Commands', desc: 'Viewers steer the battle live with danmaku commands — everyone is in the game.' },
                        { title: 'Gifts Shift the Fight', desc: 'Gift effects turn into in-game skills — tipping is playing.' },
                        { title: 'Red vs Blue Camps', desc: 'Split the room into rival camps; the will to win drives danmaku storms.' },
                        { title: 'Streamer PK Battles', desc: 'Streamers battle head-to-head live while viewers decide the outcome.' },
                    ],
                },
                {
                    tab: 'Casual Competition',
                    points: [
                        { title: 'Classic Cards & Board', desc: 'Dou Dizhu, Guandan, Mahjong and more — beloved classics ready to launch.' },
                        { title: 'Real-Time Multiplayer', desc: '1v1, 2v2 and party rooms with millisecond-level state sync.' },
                        { title: 'Instant Play', desc: 'H5 games open in seconds with no download, smooth on low-end devices.' },
                        { title: 'Leaderboards', desc: 'Ladder rankings and seasonal resets keep players coming back.' },
                    ],
                },
                {
                    tab: 'LiveOps Gameplay',
                    points: [
                        { title: 'Order & Play', desc: 'Paid orders for host co-play build a high-frequency payment scenario.' },
                        { title: 'Tickets & Predictions', desc: 'Small stakes, big wins — ticket rooms and predictions as twin revenue engines.' },
                        { title: 'Tournament Seasons', desc: 'Event buzz stretches the active cycle and retains core players.' },
                        { title: 'Cross-Room Matching', desc: 'Cross-room PK and matchmaking activate traffic across the whole platform.' },
                    ],
                },
                {
                    tab: 'Global Acceleration',
                    points: [
                        { title: 'Global Node Routing', desc: 'Acceleration nodes across five continents with intelligent best-path routing.' },
                        { title: 'Low-Latency Sync', desc: 'Millisecond-level state sync — cross-border battles with zero stutter.' },
                        { title: 'Regional Deployment', desc: 'Deploy close to target markets for compliant global expansion.' },
                    ],
                },
                {
                    tab: 'Multi-Language',
                    points: [
                        { title: 'Language Packs', desc: 'Switch between Simplified/Traditional Chinese, English, Japanese and more on demand.' },
                        { title: 'Regional Content', desc: 'Gameplay, copy and art localized for each target market.' },
                        { title: 'One-Click Translation', desc: 'Built-in admin translation brings new languages online fast.' },
                    ],
                },
            ],
        },
        fusion: {
            kicker: 'Scene Fusion',
            heading: 'Deep Fusion of "Social + Gaming" Scenarios',
            sub: 'Game content woven into every social scenario',
            tabs: [
                {
                    name: 'Voice Rooms',
                    clips: [
                        { video: '/video/保卫公主.mp4', poster: '/img/gamelistpic2/保卫公主.webp', title: 'Princess Guard', gameid: 956 },
                        { video: '/video/萌宠斗蛋仔.mp4', poster: '/img/gamelistpic2/萌宠斗蛋仔.webp', title: 'Eggy Pet Clash', gameid: 944 },
                    ],
                },
                {
                    name: 'Video Social',
                    clips: [
                        { video: '/video/雷电风云战机.mp4', poster: '/img/gamelistpic2/雷电风云战机.webp', title: 'Thunderstorm Fighter', gameid: 920 },
                        { video: '/video/奥林匹斯之门.mp4', poster: '/img/gamelistpic2/奥林匹斯.webp', title: 'Olympus', gameid: 523 },
                    ],
                },
                {
                    name: '1v1 Social',
                    clips: [
                        { video: '/video/玩转世界杯.mp4', poster: '/img/gamelistpic2/玩转世界杯.webp', title: 'World Cup Mania', gameid: 528 },
                        { video: '/img/section41/YYGame41.mp4', poster: '/img/gamelistpic2/斗地主横版.webp', title: 'Dou Dizhu' },
                    ],
                },
                {
                    name: 'Talent Shows',
                    clips: [
                        { video: '/img/section41/YYGame42.mp4', poster: '/img/gamelistpic2/骗子酒馆.webp', title: "Liar's Tavern" },
                        { video: '/img/section41/YYGame19.mp4', poster: '/img/gamelistpic2/你画我猜.webp', title: 'Draw & Guess' },
                    ],
                },
            ],
        },
        section6: {
            heading: 'Popular',
            viewAll: 'View All',
            detail: 'View Details',
            disclaimer: 'This demo is for game demonstration only. Some games may not be available for access or operation in mainland China or certain countries/regions. Please choose games in accordance with applicable local laws and regulations.',
            solutions: {
                kicker: 'LiveOps Solutions',
                heading: 'Proven Playbooks, Ready to Use',
                viewAll: 'View All Solutions',
                stepsLabel: '{count}-step flow',
            },
        },
        accel: {
            kicker: 'Global Game Acceleration',
            heading: 'Global Game Acceleration Network',
            sub: 'Nodes worldwide with intelligent routing — silky-smooth gameplay, anywhere',
            hub: 'Hong Kong Hub',
            cities: ['Los Angeles', 'Frankfurt', 'Singapore', 'Tokyo', 'São Paulo', 'Sydney', 'Dubai', 'Mumbai', 'London', 'Seoul'],
            stats: {
                games: { suffix: '', label: 'Games Served' },
                categories: { suffix: '', label: 'Content Categories' },
                languages: { value: 12, suffix: '+', label: 'Languages Covered' },
                nodes: { value: 2800, suffix: '+', label: 'Acceleration Nodes' },
            },
            footnote: 'Anchored by the Hong Kong hub, reaching Los Angeles · London · Frankfurt · Tokyo · Seoul · Singapore · Dubai · Mumbai · São Paulo · Sydney and more.',
        },
        testimonials: {
            kicker: 'Testimonials',
            heading: 'Trusted by Partners Worldwide',
            items: [
                { quote: 'After integrating the danmaku gameplay, live-room interaction rose noticeably and average watch time nearly doubled. It took only one week from kickoff to launch.', company: 'A leading voice social platform', role: 'Operations Director' },
                { quote: 'The multi-language content packs and regional deployment let us launch fast in the Middle East, and our holiday campaign cut acquisition costs by thirty percent.', company: 'A global live-streaming app', role: 'Head of Product' },
                { quote: 'The ticket-room plus prediction combo significantly lifted our paying rate — it is now our highest-ROI operations playbook.', company: 'A social entertainment platform', role: 'Head of Monetization' },
                { quote: 'Order & Play boosted both host retention and income, and the revenue-share model stays healthy — hosts now ask for more slots themselves.', company: 'A voice-chat room app', role: 'Growth Lead' },
                { quote: 'SDK integration was easier than expected — joint debugging done in three days, and the global acceleration nodes make cross-border matches feel lag-free.', company: 'An overseas casual gaming platform', role: 'CTO' },
                { quote: 'The library updates fast with a full range of categories and flexible scheduling — our long-term supplier of gamified content.', company: 'A leading live-streaming platform', role: 'Director of Ecosystem Partnerships' },
            ],
        },
        cta: {
            title: 'Ready to Grow with Gamification?',
            button: 'Get a Free Proposal',
        },
    },
}
